import {
  Box,
  ButtonBase,
  styled,
  Typography,
  useTheme
} from "@material-ui/core";
import clsx from "clsx";
import { graphql } from "gatsby";
import React from "react";
import { useToggle } from "react-use";
import { formatMultilinesText } from "../../helpers/markdown";
import PlusIcon from "../partners-flipcard/PlusIcon";
import Imgix from "react-imgix";

const List = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(3),
  gridTemplateColumns: "50% 50%",
  [theme.breakpoints.up("md")]: {
    paddingBottom: "100px"
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "100%"
  }
}));

const ImageWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "400px",
  marginBottom: theme.spacing(1),
  "& > img": {
    maxWidth: "100%",
    maxHeight: "100%"
  },
  [theme.breakpoints.down("sm")]: {
    height: "240px"
  }
}));

const CardWrapper = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  padding: theme.spacing(0, 5, 5, 5),
  borderRadius: theme.spacing(1),
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.05)",
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.up("md")]: {
    "&:nth-child(even)": {
      transform: "translateY(80px)"
    }
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4, 2.5, 6, 2.5)
  },
  "& .front": {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(4),
    opacity: 0,
    transition: "opacity 250ms ease-in-out",
    overflowY: "auto",
    "&.active": {
      opacity: 1,
      transition: "opacity 250ms ease-in-out"
    },
    [theme.breakpoints.down("sm")]: {
      paddingTop: theme.spacing(8)
    },
    [theme.breakpoints.up("md")]: {
      display: "flex",
      alignItems: "end",
      paddingRight: theme.spacing(10)
    }
  }
}));

const TitleTypography = styled(Typography)(({ theme }) => ({
  fontSize: "4rem",
  lineHeight: "4.2rem",
  marginBottom: theme.spacing(2)
}));

const CircleButton = styled(ButtonBase)(({ theme }) => ({
  width: "32px",
  height: "32px",
  background: theme.palette.grey[300],
  borderRadius: "50%",
  transform: "rotate(0deg)",
  transition: "transform 250ms ease-in-out, background 250ms ease-in-out",
  zIndex: 1,
  "&.active": {
    transform: "rotate(45deg)",
    transition: "transform 250ms ease-in-out, background 250ms ease-in-out",
    background: theme.palette.common.white
  },
  position: "absolute",
  right: theme.spacing(5),
  bottom: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    top: theme.spacing(2.5),
    bottom: "unset",
    right: theme.spacing(2.5)
  }
}));

const Card = ({
  title,
  subtitle,
  text,
  image
}: {
  title: string;
  subtitle: string;
  text: string;
  image: string;
}) => {
  const [active, toggleActive] = useToggle(false);

  return (
    <CardWrapper>
      <ImageWrapper className="image">
        <Imgix
          src={image}
          disableSrcSet
          sizes="100vw"
          imgixParams={{
            maxWidth: "800px",
            maxHeight: "400px",
            auto: "format,compress"
          }}
        />
      </ImageWrapper>

      <Box>
        <TitleTypography variant="h4" style={{ lineHeight: 1.05 }}>
          {title}
        </TitleTypography>
        <Box maxWidth={{ xs: "100%", md: "90%" }}>
          <Typography
            variant="body1"
            dangerouslySetInnerHTML={{ __html: formatMultilinesText(subtitle) }}
          />
        </Box>
      </Box>

      <CircleButton className={clsx({ active: active })} onClick={toggleActive}>
        <PlusIcon />
      </CircleButton>
      <div className={clsx({ front: true, active })}>
        <Typography
          variant="body1"
          dangerouslySetInnerHTML={{ __html: formatMultilinesText(text) }}
        />
      </div>
    </CardWrapper>
  );
};

export default function PartnersDeepTechDive({
  card1Title,
  card1Subtitle,
  card1Text,
  card1Image,
  card2Title,
  card2Subtitle,
  card2Text,
  card2Image,
  card3Title,
  card3Subtitle,
  card3Text,
  card3Image,
  card4Title,
  card4Subtitle,
  card4Text,
  card4Image
}: Queries.PartnersDeepTechDiveFragmentFragment) {
  const theme = useTheme();
  return (
    <List>
      <Card
        title={card1Title}
        subtitle={card1Subtitle}
        text={card1Text}
        image={card1Image.url}
      />
      <Card
        title={card2Title}
        subtitle={card2Subtitle}
        text={card2Text}
        image={card2Image.url}
      />
      <Card
        title={card3Title}
        subtitle={card3Subtitle}
        text={card3Text}
        image={card3Image.url}
      />
      <Card
        title={card4Title}
        subtitle={card4Subtitle}
        text={card4Text}
        image={card4Image.url}
      />
    </List>
  );
}

export const PartnersDeepTechDiveFragment = graphql`
  fragment PartnersDeepTechDiveFragment on DatoCmsCModulePartnersTechDeepDive {
    card1Title
    card1Subtitle
    card1Text
    card1Image {
      url
    }
    card2Title
    card2Subtitle
    card2Text
    card2Image {
      url
    }
    card3Title
    card3Subtitle
    card3Text
    card3Image {
      url
    }
    card4Title
    card4Subtitle
    card4Text
    card4Image {
      url
    }
    container
    innerContainer
    containerDisableGutters
    containerBackgroundColor
    model {
      apiKey
    }
  }
`;
